import React, { useMemo, ReactElement } from 'react';
import CheckAccess from '../../../components/check-access/check-access.component';
import CommentTable from '../../app/common/comment-table/comment-table.component';
import CommentForm from '../../app/common/comment-form/comment-form.component';
import SinglePageTab from '../../app/common/single-page-tab/single-page-tab.component';
import {
  ACTIVITY_COMMENT_CREATE, ACTIVITY_COMMENT_DELETE, ACTIVITY_COMMENT_EDIT, ACTIVITY_COMMENT_LIST_VIEW,
  CONTACT_VIEW, SEARCH_RESULT_VIEW,
} from '../../../constants/entity/permissions';
import { ROUTE_CONTACT, ROUTE_SEARCH } from '../../../constants/system/routes';
import { SIGN_HASH, SIGN_AT } from '../../../constants/system/signs';

const ActivityComments: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    isCommentSaving,
    currentUser,
    activityData,
    queryCommentId,
    commentText,
    commentEditId,
    commentActiveId,
    commentAttachments,
    commentAttachmentFiles,
    selectedContactCompanyContacts,
    selectedContactPersonContacts,
    tagList,
    contactCompanyList,
    contactPersonList,
    contactLinkedUserList,
    onSaveComment,
    onSwitchCommentEditMode,
    onDeleteCommentConfirm,
    onDeleteCommentCancel,
    onCommentFieldChange,
    onCommentActiveIdChange,
    onCommentFieldKeyDown,
    onCommentAttachmentFilesChange,
    onCommentAttachmentFileDelete,
    onCommentAttachmentFileError,
    onCommentEntityLinkClick,
    onContactCompanySearch,
    onContactCompanySearchClose,
    onContactPersonSearch,
    onContactPersonSearchClose,
    onCompanyContactsChange,
    onPersonContactsChange,
  } = props;

  const activityCommentList: Comment[] = activityData && activityData.comments;
  const isCommentEditing: boolean = !!commentEditId;

  const activityCommentListViewPermissions: string[] = useMemo((): string[] => [ACTIVITY_COMMENT_LIST_VIEW], []);
  const activityCommentEditPermissions: string[] = useMemo((): string[] => [ACTIVITY_COMMENT_EDIT], []);
  const activityCommentDeletePermissions: string[] = useMemo((): string[] => [ACTIVITY_COMMENT_DELETE], []);
  const activityCommentCreatePermissions: string[] = useMemo((): string[] => [ACTIVITY_COMMENT_CREATE], []);
  const commentAttachmentDropDeps: DepsItem[] = useMemo((): DepsItem[] => [
    commentAttachmentFiles,
  ], [commentAttachmentFiles]);
  const commonAttachmentFiles: (AttachmentInput | File)[] = useMemo((): (AttachmentInput | File)[] => [
    ...(commentAttachments || []),
    ...(commentAttachmentFiles || []),
  ], [commentAttachments, commentAttachmentFiles]);

  const mentionList: MentionItem[] = useMemo((): MentionItem[] => (
    contactLinkedUserList && contactLinkedUserList.length
      ? contactLinkedUserList.map((contactItem: Contact): MentionItem => ({
        id: `${SIGN_AT}${contactItem.fullName}`,
        contactId: contactItem.id,
        fullName: contactItem.fullName,
        photo: contactItem.photo,
        photoThumbnail: contactItem.photoThumbnail,
      })) : []
  ), [contactLinkedUserList]);

  const hashtagList: HashtagItem[] = useMemo((): HashtagItem[] => (
    tagList && tagList.length ? tagList.map((tagItem: Tag): HashtagItem => ({
      id: `${SIGN_HASH}${tagItem.name}`,
      tagId: tagItem.id,
      name: tagItem.name,
    })) : []
  ), [tagList]);

  return (
    <SinglePageTab withNoPadding withNoButtonBlock>
      <CheckAccess permissions={activityCommentListViewPermissions}>
        <CommentTable
          commentList={activityCommentList}
          currentUser={currentUser}
          queryCommentId={queryCommentId}
          commentEditId={commentEditId}
          commentActiveId={commentActiveId}
          editPermissions={activityCommentEditPermissions}
          deletePermissions={activityCommentDeletePermissions}
          mentionPermission={CONTACT_VIEW}
          hashtagPermission={SEARCH_RESULT_VIEW}
          onSwitchCommentEditMode={onSwitchCommentEditMode}
          onDeleteCommentConfirm={onDeleteCommentConfirm}
          onDeleteCommentCancel={onDeleteCommentCancel}
          onCommentActiveIdChange={onCommentActiveIdChange}
          onCommentEntityLinkClick={onCommentEntityLinkClick}
        />
      </CheckAccess>
      <CheckAccess permissions={activityCommentCreatePermissions}>
        <CommentForm
          isCommentSaving={isCommentSaving}
          withContactFields={!isCommentEditing}
          commentText={commentText}
          attachmentFiles={commonAttachmentFiles}
          mentionRouteRoot={ROUTE_CONTACT}
          hashtagRouteRoot={ROUTE_SEARCH}
          commentAttachmentDropDeps={commentAttachmentDropDeps}
          selectedContactCompanyContacts={selectedContactCompanyContacts}
          selectedContactPersonContacts={selectedContactPersonContacts}
          mentionList={mentionList}
          hashtagList={hashtagList}
          contactCompanyList={contactCompanyList}
          contactPersonList={contactPersonList}
          onSaveComment={onSaveComment}
          onCommentFieldChange={onCommentFieldChange}
          onCommentFieldKeyDown={onCommentFieldKeyDown}
          onCommentAttachmentFilesChange={onCommentAttachmentFilesChange}
          onCommentAttachmentFileDelete={onCommentAttachmentFileDelete}
          onCommentAttachmentFileError={onCommentAttachmentFileError}
          onContactCompanySearch={onContactCompanySearch}
          onContactCompanySearchClose={onContactCompanySearchClose}
          onContactPersonSearch={onContactPersonSearch}
          onContactPersonSearchClose={onContactPersonSearchClose}
          onCompanyContactsChange={onCompanyContactsChange}
          onPersonContactsChange={onPersonContactsChange}
        />
      </CheckAccess>
    </SinglePageTab>
  );
});

export default ActivityComments;
